'use client';

import { LayoutGrid, Rows3 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { cn } from '@/lib/utils';
import type { RecordingView } from './RecordingList';

interface ViewToggleProps {
  value: RecordingView;
  onChange: (v: RecordingView) => void;
  className?: string;
}

const OPTIONS: ReadonlyArray<{ view: RecordingView; labelKey: string; Icon: typeof LayoutGrid }> = [
  { view: 'cards', labelKey: 'viewToggle.cardView', Icon: LayoutGrid },
  { view: 'rows', labelKey: 'viewToggle.compactRows', Icon: Rows3 },
];

/**
 * Two-button segmented control for switching the recordings library between
 * the content-forward card view and the dense compact-row view. Owned by
 * `RecordingsPage` (which persists the choice); `RecordingList` only consumes
 * the resolved `view` prop.
 */
function ViewToggle({ value, onChange, className }: ViewToggleProps) {
  const t = useTranslations('recordings');
  return (
    <div
      className={cn('inline-flex items-center rounded-md border border-border', className)}
      role="group"
      aria-label={t('viewToggle.groupLabel')}
    >
      {OPTIONS.map(({ view, labelKey, Icon }, idx) => {
        const active = value === view;
        const label = t(labelKey);
        return (
          <button
            key={view}
            type="button"
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={() => onChange(view)}
            className={cn(
              'flex h-7 w-8 items-center justify-center transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              idx === 0 ? 'rounded-l-md' : 'rounded-r-md',
              active
                ? 'bg-hover text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

export { ViewToggle };
export type { ViewToggleProps };
